/**
 * Task create + edit slide-over. Mirrors the mobile add-task /
 * edit-task screens (`app/(app)/projects/[id]/add-task.tsx` /
 * `edit-task/[taskId].tsx`) — same Firestore schema, same fields.
 *
 * Used by:
 *   - Project Detail → Timeline tab "+ New Task" button (create)
 *   - Click on any Gantt bar / sidebar row / milestone table row (edit)
 *
 * Fields:
 *   - title *           (text)
 *   - category *        (chip picker — DEFAULT_TASK_CATEGORIES)
 *   - status *          (chip picker — not_started / ongoing / completed)
 *   - priority          (chip picker — low / medium / high)
 *   - startDate *       (date picker)
 *   - endDate           (date picker, optional)
 *   - progress          (0–100 slider, edit mode only)
 *   - description       (textarea)
 *
 * Calls:
 *   - `createTask` for new
 *   - `updateTask` for edit (only writes changed fields via patch)
 *   - `deleteTask` for the Delete action in edit mode (confirm first)
 */
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import { PartyPickerSlideOver } from '@/components/PartyPickerSlideOver';
import { partyTypeColors, partyTypeLabel } from '@/lib/parties';
import { uploadFilesToR2 } from '@/lib/r2Upload';
import {
  createTask,
  deleteTask,
  updateTask,
  type CreateTaskInput,
} from '@/lib/tasks';
import {
  taskStatusLabel,
  type TaskItem,
  type TaskPriority,
  type TaskStatus,
} from '@/lib/useProjectTasks';

// Mirrors mobile's DEFAULT_TASK_CATEGORIES verbatim.
const CATEGORY_OPTIONS: { key: string; label: string }[] = [
  { key: 'electrical', label: 'Electrical' },
  { key: 'plumbing', label: 'Plumbing' },
  { key: 'woodwork', label: 'Woodwork' },
  { key: 'design', label: 'Design' },
  { key: 'cleaning', label: 'Cleaning' },
  { key: 'floor_cleaning', label: 'Floor Cleaning' },
  { key: 'floor_matting', label: 'Floor Matting' },
  { key: 'painting', label: 'Painting' },
  { key: 'false_ceiling', label: 'False Ceiling' },
  { key: 'tiling', label: 'Tiling' },
  { key: 'general', label: 'General' },
];

const STATUS_OPTIONS: { key: TaskStatus; label: string }[] = [
  { key: 'not_started', label: 'Not started' },
  { key: 'ongoing', label: 'Ongoing' },
  { key: 'completed', label: 'Completed' },
];

const PRIORITY_OPTIONS: { key: TaskPriority; label: string }[] = [
  { key: 'low', label: 'Low' },
  { key: 'medium', label: 'Medium' },
  { key: 'high', label: 'High' },
];

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};

const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: 6,
};

function toIsoDate(d: Date | null | undefined): string {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isoToDate(iso: string): Date | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10));
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

export type TaskFormSlideOverProps = {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  projectId: string;
  /** When set, the slide-over opens in edit mode and seeds from the
   *  task. When null, opens in create mode. */
  task: TaskItem | null;
  /** Called after a successful save (create or edit) with the task id. */
  onSaved?: (taskId: string) => void;
  /** Called after a successful delete. */
  onDeleted?: () => void;
};

export function TaskFormSlideOver({
  open,
  onClose,
  orgId,
  projectId,
  task,
  onSaved,
  onDeleted,
}: TaskFormSlideOverProps) {
  const { user } = useAuth();
  const isEdit = Boolean(task);

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<string>('general');
  const [status, setStatus] = useState<TaskStatus>('not_started');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [progress, setProgress] = useState<number>(0);
  const [description, setDescription] = useState<string>('');
  // Party / assignee picker (mobile parity — Add Task on mobile has
  // an "Assigned to" row that opens the party picker).
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [assignedToName, setAssignedToName] = useState<string>('');
  const [assignedToType, setAssignedToType] = useState<string>('');
  const [partyPickerOpen, setPartyPickerOpen] = useState(false);

  // Reference photos (mobile parity — Add Task on mobile has a
  // "REFERENCE PHOTOS" section). Existing photos = already-uploaded
  // R2 URLs from the task doc. Staged = local Files awaiting upload
  // on save.
  const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
  const [stagedPhotos, setStagedPhotos] = useState<
    { file: File; preview: string }[]
  >([]);
  const photoInputRef = useRef<HTMLInputElement | null>(null);

  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed on open + when task changes.
  useEffect(() => {
    if (!open) return;
    if (task) {
      setTitle(task.title ?? '');
      setCategory(task.category || 'general');
      setStatus(task.status);
      setPriority(task.priority);
      setStartDate(toIsoDate(task.startDate ? new Date(task.startDate.toMillis()) : null));
      setEndDate(toIsoDate(task.endDate ? new Date(task.endDate.toMillis()) : null));
      setProgress(Math.max(0, Math.min(100, task.progress ?? 0)));
      setDescription(task.description ?? '');
      setAssignedTo(task.assignedTo ?? '');
      setAssignedToName(task.assignedToName ?? '');
      setAssignedToType('');
      setExistingPhotos(task.photoUris ?? []);
    } else {
      setTitle('');
      setCategory('general');
      setStatus('not_started');
      setPriority('medium');
      setStartDate(toIsoDate(new Date()));
      setEndDate('');
      setProgress(0);
      setDescription('');
      setAssignedTo('');
      setAssignedToName('');
      setAssignedToType('');
      setExistingPhotos([]);
    }
    // Drop any staged uploads from a previous session.
    setStagedPhotos((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.preview));
      return [];
    });
    setConfirmDelete(false);
    setError(null);
  }, [open, task]);

  // Body scroll lock + Esc.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose]);

  const handleSubmit = async () => {
    setError(null);
    if (!orgId || !user) return setError('Sign-in required.');
    if (title.trim().length < 2) return setError('Title must be at least 2 characters.');
    if (!startDate) return setError('Pick a start date.');
    const startD = isoToDate(startDate);
    if (!startD) return setError('Start date is invalid.');
    const endD = isoToDate(endDate);
    if (endD && startD && endD.getTime() < startD.getTime()) {
      return setError('End date must be on or after start date.');
    }
    setBusy(true);
    try {
      // For edits we need the task id BEFORE we upload — R2's refId
      // must match the task. For creates we have to write the doc
      // first to mint an id, then patch the photoUris on top after
      // the upload (a touch chatty but unavoidable without a
      // transaction).
      let id: string;
      if (isEdit && task) {
        let mergedPhotos = existingPhotos;
        if (stagedPhotos.length > 0) {
          const newUrls = await uploadFilesToR2({
            files: stagedPhotos.map((p) => p.file),
            kind: 'task_photo',
            refId: task.id,
            orgId,
          });
          mergedPhotos = [...existingPhotos, ...newUrls];
        }
        await updateTask(task.id, {
          title: title.trim(),
          description: description.trim(),
          status,
          priority,
          category,
          startDate: startD,
          endDate: endD ?? null,
          progress,
          assignedTo,
          assignedToName,
          photoUris: mergedPhotos,
        });
        id = task.id;
      } else {
        const payload: CreateTaskInput = {
          orgId,
          projectId,
          title: title.trim(),
          description: description.trim() || undefined,
          status,
          priority,
          category,
          startDate: startD,
          endDate: endD ?? null,
          assignedTo: assignedTo || undefined,
          assignedToName: assignedToName || undefined,
          createdBy: user.uid,
        };
        id = await createTask(payload);
        if (stagedPhotos.length > 0) {
          const newUrls = await uploadFilesToR2({
            files: stagedPhotos.map((p) => p.file),
            kind: 'task_photo',
            refId: id,
            orgId,
          });
          await updateTask(id, { photoUris: newUrls });
        }
      }
      onSaved?.(id);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const addPhotos = (files: FileList | null) => {
    if (!files) return;
    const out: { file: File; preview: string }[] = [];
    for (const f of Array.from(files)) {
      if (!f.type.startsWith('image/')) continue;
      out.push({ file: f, preview: URL.createObjectURL(f) });
    }
    const total = existingPhotos.length + stagedPhotos.length + out.length;
    setStagedPhotos((prev) => {
      const next = [...prev, ...out];
      // Cap at 6 total (existing + staged) — matches mobile.
      if (existingPhotos.length + next.length > 6) {
        const overflow = existingPhotos.length + next.length - 6;
        for (let i = 0; i < overflow; i++) {
          const removed = next.pop();
          if (removed) URL.revokeObjectURL(removed.preview);
        }
      }
      return next;
    });
    if (photoInputRef.current) photoInputRef.current.value = '';
    void total;
  };

  const removeExistingPhoto = (i: number) => {
    setExistingPhotos((prev) => {
      const next = [...prev];
      next.splice(i, 1);
      return next;
    });
  };

  const removeStagedPhoto = (i: number) => {
    setStagedPhotos((prev) => {
      const next = [...prev];
      const [removed] = next.splice(i, 1);
      if (removed) URL.revokeObjectURL(removed.preview);
      return next;
    });
  };

  const handleDelete = async () => {
    if (!task) return;
    setBusy(true);
    try {
      await deleteTask(task.id);
      onDeleted?.();
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={() => !busy && onClose()}
      />
      <div className={`slide-over${open ? ' open' : ''}`}>
        <div className="slide-header">
          <h2 className="slide-title">
            <i className="ph ph-check-square" />{' '}
            {isEdit ? 'Edit Task' : 'New Task'}
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {error ? (
            <div
              style={{
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                padding: 12,
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          ) : null}

          {/* Title */}
          <div>
            <label style={LABEL}>Title *</label>
            <input
              style={INPUT}
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
              placeholder="e.g. Install kitchen cabinets"
              autoFocus
            />
          </div>

          {/* Category */}
          <div>
            <label style={LABEL}>Category *</label>
            <ChipPicker
              options={CATEGORY_OPTIONS}
              value={category}
              onChange={setCategory}
            />
          </div>

          {/* Status */}
          <div>
            <label style={LABEL}>Status *</label>
            <ChipPicker
              options={STATUS_OPTIONS as { key: string; label: string }[]}
              value={status}
              onChange={(v) => setStatus(v as TaskStatus)}
            />
          </div>

          {/* Priority */}
          <div>
            <label style={LABEL}>Priority</label>
            <ChipPicker
              options={PRIORITY_OPTIONS as { key: string; label: string }[]}
              value={priority}
              onChange={(v) => setPriority(v as TaskPriority)}
            />
          </div>

          {/* Dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={LABEL}>Start *</label>
              <input
                type="date"
                style={INPUT}
                value={startDate}
                onChange={(e) => setStartDate(e.currentTarget.value)}
              />
            </div>
            <div>
              <label style={LABEL}>End</label>
              <input
                type="date"
                style={INPUT}
                value={endDate}
                onChange={(e) => setEndDate(e.currentTarget.value)}
                min={startDate || undefined}
              />
            </div>
          </div>

          {/* Progress slider (edit-only) */}
          {isEdit ? (
            <div>
              <label style={LABEL}>
                Progress · <span style={{ color: 'var(--text-primary)' }}>{progress}%</span>
              </label>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={progress}
                onChange={(e) => setProgress(parseInt(e.currentTarget.value, 10))}
                style={{ width: '100%' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-tertiary)' }}>
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
          ) : null}

          {/* Description */}
          <div>
            <label style={LABEL}>Description</label>
            <textarea
              style={{ ...INPUT, minHeight: 70, resize: 'vertical' }}
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              placeholder="Optional notes about this task"
            />
          </div>

          {/* Assigned to (party picker — mobile parity) */}
          <div>
            <label style={LABEL}>Assigned to</label>
            {assignedTo && assignedToName ? (
              <button
                type="button"
                onClick={() => setPartyPickerOpen(true)}
                style={{
                  ...INPUT,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  textAlign: 'left',
                }}
              >
                {(() => {
                  const c = partyTypeColors(assignedToType);
                  const ini =
                    (assignedToName
                      .trim()
                      .split(/\s+/)
                      .slice(0, 2)
                      .map((p) => p[0] ?? '')
                      .join('') || '?').toUpperCase();
                  return (
                    <>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: c.bg,
                          color: c.fg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {ini}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                          }}
                        >
                          {assignedToName}
                        </div>
                        {assignedToType ? (
                          <div
                            style={{
                              fontSize: 11,
                              color: 'var(--text-secondary)',
                            }}
                          >
                            {partyTypeLabel(assignedToType)} ·{' '}
                            <span
                              style={{
                                color: 'var(--brand)',
                                fontWeight: 600,
                              }}
                            >
                              Change
                            </span>
                          </div>
                        ) : null}
                      </div>
                      <i
                        className="ph-bold ph-caret-down"
                        style={{ color: 'var(--text-tertiary)' }}
                      />
                    </>
                  );
                })()}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setPartyPickerOpen(true)}
                style={{
                  ...INPUT,
                  cursor: 'pointer',
                  color: 'var(--text-tertiary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  textAlign: 'left',
                }}
              >
                <span>
                  <i className="ph ph-user-plus" /> Pick an assignee
                </span>
                <i className="ph-bold ph-caret-down" />
              </button>
            )}
            {assignedTo ? (
              <button
                type="button"
                onClick={() => {
                  setAssignedTo('');
                  setAssignedToName('');
                  setAssignedToType('');
                }}
                style={{
                  marginTop: 6,
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-tertiary)',
                  fontSize: 11,
                  cursor: 'pointer',
                  padding: 0,
                  fontFamily: 'inherit',
                }}
              >
                Clear assignee
              </button>
            ) : null}
          </div>

          {/* Reference photos (mobile parity) */}
          <div>
            <label style={LABEL}>
              Reference photos · {existingPhotos.length + stagedPhotos.length}
              {existingPhotos.length + stagedPhotos.length >= 6 ? ' (max)' : ' of 6'}
            </label>
            <div
              style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              {/* Already-uploaded R2 URLs */}
              {existingPhotos.map((url, i) => (
                <div
                  key={url}
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 6,
                    overflow: 'hidden',
                    position: 'relative',
                    border: '1px solid var(--border)',
                  }}
                >
                  <img
                    src={url}
                    alt="Reference"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removeExistingPhoto(i)}
                    disabled={busy}
                    aria-label="Remove photo"
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      width: 20,
                      height: 20,
                      borderRadius: 10,
                      background: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                    }}
                  >
                    <i className="ph-bold ph-x" />
                  </button>
                </div>
              ))}
              {/* Staged (new) uploads waiting for save */}
              {stagedPhotos.map((p, i) => (
                <div
                  key={p.preview}
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 6,
                    overflow: 'hidden',
                    position: 'relative',
                    border: '1.5px dashed var(--brand)',
                  }}
                  title="Will upload on save"
                >
                  <img
                    src={p.preview}
                    alt="New attachment"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removeStagedPhoto(i)}
                    disabled={busy}
                    aria-label="Remove staged photo"
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      width: 20,
                      height: 20,
                      borderRadius: 10,
                      background: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                    }}
                  >
                    <i className="ph-bold ph-x" />
                  </button>
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      fontSize: 9,
                      textAlign: 'center',
                      padding: '1px 2px',
                      fontWeight: 700,
                      letterSpacing: '0.4px',
                      textTransform: 'uppercase',
                    }}
                  >
                    NEW
                  </div>
                </div>
              ))}
              {existingPhotos.length + stagedPhotos.length < 6 ? (
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={busy}
                  title="Attach reference photos"
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 6,
                    border: '1px dashed var(--border)',
                    background: '#FAFAFA',
                    color: 'var(--brand)',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                    fontFamily: 'inherit',
                    fontSize: 10,
                    fontWeight: 600,
                  }}
                >
                  <i className="ph ph-plus" style={{ fontSize: 18 }} />
                  Add
                </button>
              ) : null}
            </div>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => addPhotos(e.currentTarget.files)}
            />
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-tertiary)',
                marginTop: 6,
              }}
            >
              {stagedPhotos.length > 0
                ? `${stagedPhotos.length} new photo${stagedPhotos.length === 1 ? '' : 's'} will upload on save.`
                : 'Up to 6 photos · JPG / PNG / HEIC supported.'}
            </div>
          </div>

          {/* Footer actions */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 8,
              paddingTop: 16,
              borderTop: '1px solid var(--border)',
            }}
          >
            {isEdit ? (
              confirmDelete ? (
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontSize: 12, color: 'var(--critical)' }}>
                    Delete this task?
                  </span>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setConfirmDelete(false)}
                    disabled={busy}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleDelete}
                    disabled={busy}
                    style={{
                      background: 'var(--critical)',
                      borderColor: 'var(--critical)',
                    }}
                  >
                    Yes, delete
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy}
                  style={{ color: 'var(--critical)' }}
                >
                  <i className="ph ph-trash" /> Delete
                </button>
              )
            ) : (
              <div />
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmit}
                disabled={busy}
              >
                {busy
                  ? stagedPhotos.length > 0
                    ? `Uploading ${stagedPhotos.length} photo${stagedPhotos.length === 1 ? '' : 's'}…`
                    : 'Saving…'
                  : isEdit
                    ? 'Save changes'
                    : 'Create task'}
              </button>
            </div>
          </div>

          {/* Status reference (read-only legend, edit mode only) */}
          {isEdit && task ? (
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-tertiary)',
                marginTop: 8,
              }}
            >
              Currently <b>{taskStatusLabel(task.status)}</b> · last updated{' '}
              {task.updatedAt?.toMillis
                ? new Date(task.updatedAt.toMillis()).toLocaleString('en-IN')
                : '—'}
            </div>
          ) : null}
        </div>
      </div>

      {/* Party picker — sets assignee to a party doc. Same picker
          used on the transaction form so picks one source of truth. */}
      <PartyPickerSlideOver
        open={partyPickerOpen}
        onClose={() => setPartyPickerOpen(false)}
        orgId={orgId}
        defaultName=""
        onPick={(p) => {
          setAssignedTo(p.id);
          setAssignedToName(p.name);
          setAssignedToType(p.partyType);
          setPartyPickerOpen(false);
        }}
      />
    </>
  );
}

// ───── Chip picker ─────

function ChipPicker({
  options,
  value,
  onChange,
}: {
  options: { key: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map((o) => {
        const active = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            style={{
              padding: '6px 12px',
              border: active
                ? '1.5px solid var(--brand)'
                : '1px solid var(--border)',
              background: active ? 'var(--brand-bg)' : '#fff',
              color: active ? 'var(--brand)' : 'var(--text-primary)',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: active ? 700 : 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
